'use client';

import React, { useState } from 'react';

import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { OrderBaseUrls, OrderLineOpenedFrom, Status } from '@ravago/shared/page-data/models/elements';
import { PermissionResources } from '@ravago/shared/page-data/models/features';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Chip } from '@ravago/shared/radiance/components/chip/components/Chip/Chip';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Expand } from '@ravago/shared/radiance/components/expand/components/Expand/Expand';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { usePermission } from '../../../hooks/use-permission';
import { DynamicPageLink } from '../../content/dynamic-page-link/dynamic-page-link';
import { Link } from '../link/link';
import { DeliveryInformation } from '../order/delivery-information';

import { OrderLineButtons } from './components/order-line-buttons';
import { OrderLineDetailsTable } from './components/order-line-details-table';
import { OrderLineDocuments } from './components/order-line-documents';
import { ShipmentsInfo } from './components/shipments-info';
import { useOrderLine } from './hooks/use-order-line';

interface Props {
   locale: string;
   isUSBusinessContext: boolean;
   fullOrderNumber: string;
   contentfulProduct: NormalizedProduct | undefined;
   openedFrom: OrderLineOpenedFrom;
   orderBaseUrls: OrderBaseUrls;
   showInvoiceDocuments: boolean;
   requestCoa: (orderNumber: string, producerLotNr: string) => Promise<boolean>;
   requestDeliveryNote: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   requestBol: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   downloadDocument: (downloadEndpoint: string, correlationId: string) => Promise<string | undefined>;
}

const mapToTranslation: { [key in Status]: string } = {
   'Order Received': 'shipment.status.order-received',
   Planned: 'shipment.status.processing',
   Processing: 'shipment.status.processing',
   'Order Confirmed': 'shipment.status.order-confirmed',
   'Transport Arranged': 'shipment.status.transport-arranged',
   'In Transit': 'shipment.status.in-transit',
   Closed: 'shipment.status.closed',
   Invoiced: 'order.status.invoiced',
   Credited: 'order.status.credited',
   'Awaiting Call Off': 'order.status.awaiting-call-off',
   'Call Off Overdue': 'order.status.call-off-overdue'
};

export const OrderLineFlyout: React.FC<Props> = ({
   locale,
   isUSBusinessContext,
   fullOrderNumber,
   contentfulProduct,
   openedFrom,
   requestDeliveryNote,
   orderBaseUrls,
   showInvoiceDocuments,
   requestBol,
   requestCoa,
   downloadDocument
}: Props) => {
   const translate = useTranslations();
   const format = useFormatter();

   const [orderId, orderNumber] = fullOrderNumber.split('-').map(Number);

   const {
      orderLine,
      orderLineDocuments,
      hasDocuments,
      isLoading: orderLineLoading
   } = useOrderLine({
      orderId,
      orderNumber,
      locale,
      contentfulProduct
   });
   const { hasPermission: canViewPrices, isLoading: permissionLoading } = usePermission(PermissionResources.ORDER_PRICE);

   const [isAccordionOpen, setIsAccordionOpen] = useState(false);

   const isLoading = orderLineLoading || permissionLoading;

   const handleAccordionToggle = () => {
      setIsAccordionOpen(!isAccordionOpen);
   };

   if (isLoading || !orderLine) {
      return (
         <Box direction="column" items="center">
            <Spinner />
         </Box>
      );
   }

   return (
      <Box direction="column" gap="md">
         <Box direction="column">
            <Box justify="between" items="center">
               {orderLine.status && (
                  <Badge size="regular" variant="info" weight="bold">
                     {translate(mapToTranslation[orderLine.status])}
                  </Badge>
               )}
               {orderLine.deliveryDate && (
                  <Chip
                     type={
                        <Typography variant="small" component="span" color="strong">
                           {translate('page-consumer.order-line-flyout.estimated-delivery')}
                        </Typography>
                     }
                     label={
                        <Typography variant="small-highlight" component="span" align="left" color="strong">
                           {DateUtils.transformOrderDeliveryDate(format, orderLine.deliveryDate)}
                        </Typography>
                     }
                  />
               )}
            </Box>
            <Box direction="column" gap="md">
               <Box items="center">
                  <Avatar size="large" shape="square" src={orderLine.contentfulProduct?.brand?.producer?.logo?.url} />
                  <Box direction="column" gap="none">
                     {orderLine.contentfulProduct ? (
                        <DynamicPageLink
                           id={`dynamic-link__order-line__${orderLine.contentfulProduct.slug}`}
                           locale={locale}
                           baseRoute={{
                              url: orderBaseUrls.productBaseUrl ?? undefined,
                              noFollow: false
                           }}
                           slug={orderLine.contentfulProduct.slug}
                        >
                           <Typography component="span" variant="body-highlight" color="primary">
                              {orderLine.product.description}
                           </Typography>
                        </DynamicPageLink>
                     ) : (
                        <Typography component="span" variant="body-highlight" color="primary">
                           {orderLine.product.description}
                        </Typography>
                     )}
                     {orderLine.customerProductReference && (
                        <Typography component="span" variant="body">
                           {orderLine.customerProductReference}
                        </Typography>
                     )}
                  </Box>
               </Box>
            </Box>
         </Box>
         <OrderLineDetailsTable orderLine={orderLine} canViewPrices={canViewPrices} />
         <OrderLineButtons
            showOrderDetailButton={openedFrom !== 'order_detail'}
            showCallOffAgreementButton={openedFrom !== 'call_off_agreement'}
            locale={locale}
            orderId={orderId.toString()}
            orderBaseUrls={orderBaseUrls}
            agreementNumber={orderLine.agreementNumber}
         />
         <Divider />
         <Expand
            expanded={isAccordionOpen}
            header={
                
               <Link underline="none" onClick={handleAccordionToggle}>
                  <Box direction="row" justify="between" items="center">
                     <Box>
                        <Typography component="h4" variant="heading-4" color="strong">
                           {translate('page-consumer.order-line-flyout.shipment-information')}
                        </Typography>
                        {orderLine.shipmentIds && orderLine.shipmentIds.length > 1 && (
                           <Badge size="regular" variant="primary">
                              {orderLine.shipmentIds.length}
                           </Badge>
                        )}
                     </Box>

                     <Icon icon={isAccordionOpen ? 'chevron-up' : 'chevron-down'} />
                  </Box>
               </Link>
            }
         >
            <Box direction="column" spacing={{ top: 'md' }} gap="md">
               {orderLine.shipmentIds && orderLine.shipmentIds.length > 0 && openedFrom !== 'shipment_detail' && (
                  <ShipmentsInfo shipmentIds={orderLine.shipmentIds} locale={locale} baseRoute={orderBaseUrls.shipmentBaseUrl ?? undefined} />
               )}
               <DeliveryInformation
                  destinationAddress={orderLine.destinationAddress}
                  deliveryTermAddress={orderLine.deliveryTermAddress}
                  deliveryInstructions={orderLine.deliveryInstructions}
                  deliveryTerm={orderLine.deliveryTerm}
                  isInFlyout
               />
            </Box>
         </Expand>
         <Divider />
         {hasDocuments && (
            <OrderLineDocuments
               orderLine={orderLine}
               documents={orderLineDocuments}
               isUSBusinessContext={isUSBusinessContext}
               showInvoiceDocuments={showInvoiceDocuments}
               requestCoa={requestCoa}
               requestDeliveryNote={requestDeliveryNote}
               requestBol={requestBol}
               downloadDocument={downloadDocument}
            />
         )}
      </Box>
   );
};
